import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Alert,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../../../../../../../formik/FormikDecimalTextField";
import { FormikYesNoAutocomplete } from "../../../../../../../../../../formik/FormikYesNoAutocomplete";
import { FormikAutocompleteSelect } from "../../../../../../../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { AssetIncomeFormValues, IncomeEntryFormValue, IncomeMonthFormValue } from "../../../../../../IncomeView.types";
import {
  calculateMonthlyIncome,
  formatMonthKey,
  hasDuplicateMonths,
  selectableMonthKeys,
} from "../../../../../../IncomeView.utils";
import { ReadOnlyAmount } from "../read-only-amount/ReadOnlyAmount";

const SECTION = "clients.client.debtSettlement.assetInventory.incomeTab.months";

const ROW_HEADER_WIDTH = "34%";

const ROW_HEADER_SX = { verticalAlign: "top", pt: 4, width: ROW_HEADER_WIDTH } as const;
const FIELD_CELL_SX = { verticalAlign: "top", pt: 2.75 } as const;
const COLUMN_HEADER_SX = {
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "text.secondary",
  fontWeight: 600,
} as const;

type MonthAmountField = keyof Pick<
  IncomeMonthFormValue,
  "paidAmount" | "garnishedAmount" | "expenseAllowance" | "familyAllowance" | "employerSettledExpenses"
>;

interface Props {
  formik: FormikProps<AssetIncomeFormValues>;
  index: number;
  entry: IncomeEntryFormValue;
  isNonSeizable: boolean;
}

export function IncomeMonthTable({ formik, index, entry, isNonSeizable }: Props) {
  const { t } = useTranslation();

  const withSpecialPayments = entry.hasSpecialPayments === true;
  const isGarnished = !isNonSeizable && entry.isPartiallyGarnished === true;
  const tableId = `income-${entry.key}-months`;

  const monthOptions = useMemo(
    () =>
      selectableMonthKeys(
        new Date(),
        entry.months.map((month) => month.month)
      ).map((key) => ({ id: key, label: formatMonthKey(key) })),
    [entry.months]
  );

  const monthPath = (month: number, field: keyof IncomeMonthFormValue) =>
    `incomes.${index}.months.${month}.${field}` as const;

  const columnId = (month: number) => `${tableId}-column-${month}`;
  const rowId = (row: string) => `${tableId}-row-${row}`;
  const cellLabelledBy = (row: string, month: number) => `${rowId(row)} ${columnId(month)}`;

  const placeholderOf = (month: number) => t(`${SECTION}.monthColumn`, { number: month + 1 });

  const columnLabel = (month: number) => {
    const key = entry.months[month]?.month ?? null;
    return key ? formatMonthKey(key) : placeholderOf(month);
  };

  const rowHeader = (row: string, label: string) => (
    <TableCell component="th" sx={ROW_HEADER_SX}>
      <Typography id={rowId(row)} variant="body2">
        {label}
      </Typography>
    </TableCell>
  );

  const amountRow = (field: MonthAmountField, label: string): ReactNode => (
    <TableRow key={field}>
      {rowHeader(field, label)}
      {entry.months.map((_, month) => (
        <TableCell key={month} sx={FIELD_CELL_SX}>
          <FormikDecimalTextField
            formik={formik}
            path={monthPath(month, field)}
            decimalScale={2}
            minValue={0}
            slotProps={{
              input: { endAdornment: <InputAdornment position="end">€</InputAdornment> },
              htmlInput: { "aria-labelledby": cellLabelledBy(field, month) },
            }}
          />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2" component="h4">
        {t(`${SECTION}.title`)}
      </Typography>
      <TableContainer>
        <Table size="small" aria-label={t(`${SECTION}.title`)} sx={{ tableLayout: "fixed", minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={{ width: ROW_HEADER_WIDTH }} />
              {entry.months.map((_, month) => (
                <TableCell key={month}>
                  <Typography id={columnId(month)} variant="caption" sx={COLUMN_HEADER_SX}>
                    {columnLabel(month)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {rowHeader("month", t(`${SECTION}.month`))}
              {entry.months.map((_, month) => (
                <TableCell key={month} sx={FIELD_CELL_SX}>
                  <FormikAutocompleteSelect
                    formik={formik}
                    path={monthPath(month, "month")}
                    options={monthOptions}
                    textFieldProps={{ placeholder: placeholderOf(month) }}
                    ariaLabelledBy={cellLabelledBy("month", month)}
                  />
                </TableCell>
              ))}
            </TableRow>
            {amountRow("paidAmount", t(`${SECTION}.paidAmount`))}
            {isGarnished && amountRow("garnishedAmount", t(`${SECTION}.garnishedAmount`))}
            {withSpecialPayments &&
              entry.hasExpenseAllowances === true &&
              amountRow("expenseAllowance", t(`${SECTION}.expenseAllowance`))}
            {withSpecialPayments &&
              entry.hasEmployerFamilyAllowance === true &&
              amountRow("familyAllowance", t(`${SECTION}.familyAllowance`))}
            {withSpecialPayments &&
              entry.hasEmployerSettledExpenses === true &&
              amountRow("employerSettledExpenses", t(`${SECTION}.employerSettledExpenses`))}
            {withSpecialPayments && (
              <TableRow>
                {rowHeader("specialPaymentPaid", t(`${SECTION}.specialPaymentPaid`))}
                {entry.months.map((_, month) => (
                  <TableCell key={month} sx={FIELD_CELL_SX}>
                    <FormikYesNoAutocomplete
                      formik={formik}
                      path={monthPath(month, "specialPaymentPaid")}
                      ariaLabelledBy={cellLabelledBy("specialPaymentPaid", month)}
                    />
                  </TableCell>
                ))}
              </TableRow>
            )}
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell component="th" sx={{ width: ROW_HEADER_WIDTH, verticalAlign: "middle" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(isNonSeizable ? `${SECTION}.monthlyIncome` : `${SECTION}.relevantMonthlyIncome`)}
                </Typography>
              </TableCell>
              {entry.months.map((month, monthIndex) => (
                <TableCell key={monthIndex} aria-live="polite" sx={{ verticalAlign: "middle", py: 1.5 }}>
                  <ReadOnlyAmount amount={calculateMonthlyIncome(entry, month, isNonSeizable)} />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {hasDuplicateMonths(entry) && (
        <Alert severity="error" role="alert">
          {t("clients.client.debtSettlement.assetInventory.incomeTab.validation.duplicateMonth")}
        </Alert>
      )}
    </Stack>
  );
}
